type KeyboardAnchorFilenameParams = {
  activeFilename: string | null;
  topVisibleFilename: string | null;
  isActiveVisible: boolean;
};

type ViewportActiveFilenameParams = {
  currentFilename: string | null;
  renderedFilenames: string[];
  viewportTopOffset: number;
  activationThreshold: number;
  viewportBottom: number;
  candidates: Array<{
    filename: string;
    top: number;
    bottom: number;
  }>;
};

/**
 * Resolves the active filename against the currently rendered file list.
 * Returns null when there is no explicit selection.
 */
export function resolveActiveFilename(
  requestedFilename: string | null,
  renderedFilenames: string[],
): string | null {
  if (requestedFilename && renderedFilenames.includes(requestedFilename)) {
    return requestedFilename;
  }

  return null;
}

/**
 * Returns the next filename in rendered order, wrapping at list boundaries.
 */
export function getNextFilenameInOrder(
  renderedFilenames: string[],
  activeFilename: string | null,
  offset: -1 | 1,
): string | null {
  const totalFiles = renderedFilenames.length;
  if (!totalFiles) {
    return null;
  }

  const activeIndex = activeFilename ? renderedFilenames.indexOf(activeFilename) : -1;
  if (activeIndex === -1) {
    return renderedFilenames[offset === 1 ? 0 : totalFiles - 1] ?? null;
  }

  return renderedFilenames[(activeIndex + offset + totalFiles) % totalFiles] ?? null;
}

/**
 * The first file after the anchor that isn't viewed, wrapping around to the start, so the anchor
 * itself comes last. Null when every file is viewed. An anchor outside the order starts the search
 * at the first file.
 */
export function getNextUnviewedFilename(
  orderedFilenames: readonly string[],
  viewed: ReadonlySet<string>,
  anchor: string | null,
): string | null {
  const anchorIndex = anchor === null ? -1 : orderedFilenames.indexOf(anchor);

  for (let offset = 1; offset <= orderedFilenames.length; offset += 1) {
    const filename = orderedFilenames[(anchorIndex + offset) % orderedFilenames.length];
    if (filename !== undefined && !viewed.has(filename)) {
      return filename;
    }
  }

  return null;
}

/**
 * Chooses the keyboard navigation anchor.
 * We prefer the current active file while it is visible, and otherwise anchor to
 * the topmost visible file to avoid "skipping" after manual scrolling.
 */
export function resolveKeyboardAnchorFilename({
  activeFilename,
  topVisibleFilename,
  isActiveVisible,
}: KeyboardAnchorFilenameParams): string | null {
  if (activeFilename && isActiveVisible) {
    return activeFilename;
  }

  if (topVisibleFilename) {
    return topVisibleFilename;
  }

  return activeFilename;
}

/**
 * Chooses the selected file from viewport state using a sticky top-anchor rule.
 * We prefer the visible file whose header is closest to the sticky-header anchor
 * once it enters the activation band. If no file has crossed into that band yet,
 * we keep the current visible file selected to avoid eager switching.
 */
export function resolveViewportActiveFilename({
  currentFilename,
  renderedFilenames,
  viewportTopOffset,
  activationThreshold,
  viewportBottom,
  candidates,
}: ViewportActiveFilenameParams): string | null {
  let firstVisibleFilename: string | null = null;
  let currentIsVisible = false;
  let closestBandFilename: string | null = null;
  let closestBandDistance = Number.POSITIVE_INFINITY;

  candidates.forEach((candidate) => {
    const isVisible = candidate.bottom > viewportTopOffset && candidate.top < viewportBottom;
    if (!isVisible) {
      return;
    }

    if (!firstVisibleFilename) {
      firstVisibleFilename = candidate.filename;
    }

    if (candidate.filename === currentFilename) {
      currentIsVisible = true;
    }

    if (candidate.top <= viewportTopOffset + activationThreshold) {
      const distanceFromAnchor = Math.abs(candidate.top - viewportTopOffset);
      if (distanceFromAnchor < closestBandDistance) {
        closestBandDistance = distanceFromAnchor;
        closestBandFilename = candidate.filename;
      }
    }
  });

  if (closestBandFilename) {
    return closestBandFilename;
  }

  if (currentFilename && currentIsVisible) {
    return currentFilename;
  }

  if (firstVisibleFilename) {
    return firstVisibleFilename;
  }

  if (currentFilename && renderedFilenames.includes(currentFilename)) {
    return currentFilename;
  }

  return renderedFilenames[0] ?? null;
}

/**
 * Resolves a group navigation request to the group's first rendered filename.
 */
export function resolveGroupTargetFilename(
  groupId: string,
  firstFilenameByGroupId: Map<string, string>,
): string | null {
  if (!groupId) {
    return null;
  }

  return firstFilenameByGroupId.get(groupId) ?? null;
}

/**
 * Resolves comment navigation to a filename, preferring explicit context when provided.
 */
export function resolveCommentTargetFilename(
  commentId: string,
  contextFilename: string | undefined,
  commentIdToFilename: Map<string, string>,
): string | null {
  if (contextFilename) {
    return contextFilename;
  }

  return commentIdToFilename.get(commentId) ?? null;
}
